import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Box, Play, Square, RotateCw, Trash2, Plus, Search } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'
import StatusBadge from '../components/StatusBadge'
import { getContainers, startContainer, stopContainer, restartContainer, destroyContainer } from '../lib/api'
import type { Container } from '../types'

export default function Containers() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'running' | 'stopped'>('all')

  const { data: containers = [], isLoading } = useQuery({
    queryKey: ['containers'],
    queryFn: getContainers,
    refetchInterval: 5000,
  })

  const startMutation = useMutation({
    mutationFn: startContainer,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['containers'] }),
  })

  const stopMutation = useMutation({
    mutationFn: stopContainer,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['containers'] }),
  })

  const restartMutation = useMutation({
    mutationFn: restartContainer,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['containers'] }),
  })

  const destroyMutation = useMutation({
    mutationFn: destroyContainer,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['containers'] }),
  })

  const filteredContainers = containers.filter((container: Container) => {
    const matchesSearch = container.id.toLowerCase().includes(search.toLowerCase())
    const matchesFilter =
      filter === 'all' ||
      (filter === 'running' && container.state === 'running') ||
      (filter === 'stopped' && container.state !== 'running')
    return matchesSearch && matchesFilter
  })

  const handleAction = async (action: 'start' | 'stop' | 'restart' | 'destroy', id: string) => {
    if (action === 'destroy' && !confirm(`Are you sure you want to destroy ${id}?`)) {
      return
    }

    switch (action) {
      case 'start':
        startMutation.mutate(id)
        break
      case 'stop':
        stopMutation.mutate(id)
        break
      case 'restart':
        restartMutation.mutate(id)
        break
      case 'destroy':
        destroyMutation.mutate(id)
        break
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Containers</h1>
          <p className="mt-1 text-gray-500">Manage Voltainer containers</p>
        </div>
        <Button icon={<Plus className="h-4 w-4" />}>
          Create Container
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search containers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-voltainer-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center space-x-2">
            {(['all', 'running', 'stopped'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === f
                    ? 'bg-voltainer-100 text-voltainer-700'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Container List */}
      {isLoading ? (
        <Card>
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-voltainer-600" />
          </div>
        </Card>
      ) : filteredContainers.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Box className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No containers found</h3>
            <p className="mt-2 text-gray-500">
              {search || filter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create a container to get started'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredContainers.map((container: Container) => (
            <Card key={container.id} padding="none">
              <div className="flex items-center justify-between p-4">
                <Link
                  to={`/containers/${encodeURIComponent(container.id)}`}
                  className="flex items-center space-x-4 flex-1 min-w-0"
                >
                  <div className="flex-shrink-0 p-2 bg-gray-100 rounded-lg">
                    <Box className="h-6 w-6 text-voltainer-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{container.id}</p>
                    {container.ip_address && (
                      <p className="text-sm text-gray-500">IP: {container.ip_address}</p>
                    )}
                  </div>
                </Link>

                <div className="flex items-center space-x-3">
                  <StatusBadge status={container.state} />

                  <div className="flex items-center space-x-1">
                    {container.state !== 'running' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAction('start', container.id)}
                        loading={startMutation.isPending}
                        title="Start"
                      >
                        <Play className="h-4 w-4 text-green-600" />
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAction('stop', container.id)}
                          loading={stopMutation.isPending}
                          title="Stop"
                        >
                          <Square className="h-4 w-4 text-gray-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAction('restart', container.id)}
                          loading={restartMutation.isPending}
                          title="Restart"
                        >
                          <RotateCw className="h-4 w-4 text-blue-600" />
                        </Button>
                      </>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleAction('destroy', container.id)}
                      loading={destroyMutation.isPending}
                      title="Destroy"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
